import moment from "moment";
import { IProjectAOI } from "../interfaces/i-project-aoi";
import { IMetadataImage } from "../interfaces/i-metadataimage";
import { ImagesService } from "../services/internal-services/images.service";
import { ViewerConstants } from "../../shared/model/viewer-constants";
import { Viewerlayer } from "./viewerlayer";

const isWide = (layer: string): boolean => {
  return layer.toLowerCase().includes("wide") || layer.toLowerCase().includes("high");
};
const isGray = (layerName): boolean => {
  return layerName?.indexOf("gray") !== -1;
};

export const Collection: (
  name: string,
  prettyName: string,
  layer: Viewerlayer, //always null
  minDate: Date,
  maxDate: Date,
  maxZoom: number,
  averageGsd: number,
  aoiOblique: boolean,
  nadirDate: Date,
  collectionName?: string,
  collectionType?: string,
  orientations?: string[],
  images?: IMetadataImage[],
  geometry?: any,
  enabled?: boolean,
  layerName?: string,
  sourceLayer?: string,
  coverageType?: string,
  visible?: boolean,
  cirAvailable?: boolean,
) => IProjectAOI = (
  name: string,
  prettyName: string,
  layer: Viewerlayer,
  minDate: Date,
  maxDate: Date,
  maxZoom: number,
  averageGsd: number,
  aoiOblique: boolean,
  newestNadirDate: Date,
  collectionName?: string,
  collectionType?: string,
  orientations?: string[],
  images?: IMetadataImage[],
  geometry?: any,
  enabled?: boolean,
  layerName?: string,
  sourceLayer?: string,
  coverageType?: string,
  visible?: boolean,
  cirAvailable?: boolean,
) => ({
  name,
  layer,
  minDate,
  maxDate,
  maxZoom,
  averageGsd,
  aoiOblique,
  nadirDate: newestNadirDate,
  collectionName,
  collectionType,
  orientations,
  images,
  geometry,
  enabled,
  layerName,
  sourceLayer,
  coverageType,
  visible,
  cirAvailable,
  isCirAvailable: (is3D: boolean) => {
    if (!is3D && cirAvailable !== undefined) {
      return cirAvailable;
    } else {
      return images
        ? images.some((img) => img.orientation === ViewerConstants.DISCRETE_ORIENTATIONS.I && (!is3D || !img.archived))
        : false;
    }
  },
  hasChilds: () => {
    return false;
  },
  getLayerName: () => {
    return layerName || null;
  },
  getSourceLayer: () => {
    return sourceLayer || layerName;
  },
  getName: () => {
    return name;
  },
  getParentAOI: () => {
    return collectionName || null;
  },
  hasOrientation: (orientation) => {
    return orientations.includes(orientation);
  },
  hasNonArchivedOrientation: (orientation) => {
    return images ? images.some((img) => img.orientation === orientation && !img.archived) : false;
  },
  hasObliques: () => {
    return orientations?.some((o) => ViewerConstants.OBLIQUE_ORIENTATIONS.includes(o));
  },
  hasInfrared: () => {
    //todo mock
    return true;
  },
  isEnabled: () => {
    return enabled;
  },
  isEnabled3D: () => {
    //todo mock
    return aoiOblique && orientations?.some((o) => ViewerConstants.OBLIQUE_ORIENTATIONS.includes(o));
  },
  isAOIOblique: () => {
    return aoiOblique;
  },
  isVisible: () => {
    //todo mock
    return !!visible;
  },
  isParentVisible: () => {
    return true;
  },
  isCollectionVisible: () => {
    return true;
  },
  isGraySky: () => {
    return isGray(sourceLayer || layerName);
  },
  isWide: () => {
    return isWide(sourceLayer || layerName);
  },
  isUrban: () => {
    return (
      !isWide(sourceLayer || layerName) &&
      ((sourceLayer || layerName)?.toLowerCase().indexOf("urban") !== -1 ||
        (sourceLayer || layerName)?.toLowerCase().indexOf("bluesky") !== -1)
    );
  },
  isUrbanR: () => {
    return !isWide(sourceLayer || layerName) && (sourceLayer || layerName)?.toLowerCase().indexOf("urban-r") !== -1;
  },
  isLegacy: () => {
    return (sourceLayer || layerName)?.toLowerCase().indexOf("-g") !== -1;
  },
  getOrder: () => {
    return null;
  },
  getGroupOrder: () => {
    return null;
  },
  getParentGroupOrder: () => {
    return null;
  },
  getAvgGsd: () => {
    return averageGsd || null;
  },
  getCoverageType: () => {
    return coverageType || null;
  },
  isChildAOI: () => {
    return false;
  },
  isAOI: () => {
    return true;
  },
  getLayerId: () => {
    return layerName + name;
  },
  getCollectionId: () => {
    return null;
  },
  getCollectionType: () => {
    return collectionType;
  },
  getParentId: () => {
    return null;
  },
  getDescription: () => {
    return name + layerName || null;
  },
  getLayer: () => {
    return layerName;
  },
  hasImage: (imageId: string) => {
    return images && images.findIndex((img) => img.image_id === imageId) !== -1;
  },
  hasImages: () => {
    return !!images?.length;
  },
  getNewestImage: (orientation: string = null) => {
    if (!images || !images.length) {
      return null;
    }
    let filtered = orientation
      ? images.filter((img) => ImagesService.normalizeOrientation(img.orientation) === orientation)
      : images;

    filtered = filtered.sort(
      (i1: IMetadataImage, i2: IMetadataImage) =>
        ImagesService.getCaptureDate(i2).getTime() - ImagesService.getCaptureDate(i1).getTime(),
    );
    return filtered[0] || null;
  },
  // contains: (point: Point) => {
  //   return GeoService.polygonContainsPoint(geometry, point);
  // },
  getPrettyDate: () => {
    if (newestNadirDate) {
      return `${moment(newestNadirDate).format("MMM DD, YYYY")}`;
    }
    const minMonth = maxDate ? moment(minDate).format("MMM") : "";
    const maxMonth = maxDate ? moment(maxDate).format("MMM") : "";
    if ((sourceLayer || layerName).indexOf("graysky") !== -1) {
      return `${minMonth !== maxMonth ? `${minMonth} - ` : ""} ${`${maxMonth} ${moment(maxDate).format("DD, YYYY")}`}`;
    }
    return `${minMonth !== maxMonth ? `${minMonth} - ` : ""} ${`${maxMonth} ${moment(maxDate).format("YYYY")}`}`;
  },
  getYearShortFormat: () => {
    const baseDate = newestNadirDate ?? maxDate; // ambos deberían ser Date
    return `\'${moment(baseDate).format("YY")}`;
  },
  getPrettyCollectionName: () => {
    return name || null;
  },
  getPrettyName: () => {
    return prettyName || name;
  },
  getParentPrettyName: () => {
    return name || null;
  },
  getMaxDate: (format: string = "MMM DD, YYYY") => {
    return `${moment(maxDate).format(format)}`;
  },
  getMaxZoom: () => {
    return maxZoom;
  },
  getYear: () => {
    const baseDate = newestNadirDate ?? maxDate; // ambos deberían ser Date
    return `${moment(baseDate).format("YYYY")}`;
  },
  getProject: () => {
    return this;
  },
  enabledContrast: () => {
    //todo we shouldn't store enabled contrast state here
    return false;
  },
  allowDamageAssessment: () => {
    const year = moment(maxDate).format("YYYY");
    const isAfter2022 = year ? Number(year) >= ViewerConstants.DAMAGE_ASSESSMENT_MIN_YEAR : false;
    return isGray(sourceLayer || layerName) && isAfter2022;
  },
  setVisible: (value: boolean) => {
    visible = value;
  },
});
